import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";

const framework = [
  {
    step: "01",
    title: "剧情事实",
    description: "先厘清发生了什么，避免以立场替代叙述，让后续分析建立在可靠的情节基础上。",
  },
  {
    step: "02",
    title: "人物动机",
    description: "追问角色为什么这样选择，并承认动机往往是混合的，包含自利、恐惧与善意。",
  },
  {
    step: "03",
    title: "伦理处境",
    description: "呈现多方立场及其代价，不急于给出道德裁决，把判断空间留给读者。",
  },
  {
    step: "04",
    title: "文化背景",
    description: "补充作品所处的社会语境与创作传统，说明某些选择为何在此处成立。",
  },
];

const team = [
  {
    name: "编辑组 · 林澈",
    role: "主编 / 影视评论",
    bio: "负责作品档案的整体架构与解析方向，关注家庭伦理与代际叙事。",
  },
  {
    name: "编辑组 · 苏见",
    role: "责任编辑 / 产业观察",
    bio: "追踪行业动态与平台变化，撰写季度片单与产业趋势分析。",
  },
  {
    name: "研究组 · 周予",
    role: "文化研究 / 专题策划",
    bio: "负责专题合集的议题设计，研究影像语言与空间叙事。",
  },
  {
    name: "技术组 · 陈默",
    role: "资料整理 / 档案维护",
    bio: "维护作品元信息与检索标签，确保每一条记录可追溯、可筛选。",
  },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 日本伦理影视资料站",
    description:
      "日本伦理是一个专注日本伦理题材影视作品的资料型网站，提供作品档案、剧情解析与行业资讯。本页介绍站点定位、编辑方法、团队与版权声明。",
    keywords: "日本伦理,关于我们,影视资料站,编辑原则,伦理影视解析",
  });

  return (
    <>
      <section className="relative overflow-hidden border-b border-background-800 bg-background-900">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=cinematic%20wide%20shot%20of%20a%20quiet%20archive%20room%20with%20wooden%20shelves%20of%20film%20canisters%20and%20index%20cards%2C%20single%20warm%20desk%20lamp%2C%20deep%20navy%20shadows%2C%20dust%20in%20the%20air%2C%20editorial%20film%20still%2C%20no%20text&width=1600&height=700&seq=about-01&orientation=landscape"
            alt="资料档案室氛围图"
            className="h-full w-full object-cover object-center opacity-45"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-background-950 via-background-950/90 to-background-950/70"></div>
        </div>
        <div className="relative mx-auto max-w-[1440px] px-4 pb-12 pt-24 md:px-12 md:pb-16 md:pt-32">
          <nav className="flex flex-wrap items-center gap-2 text-xs text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-accent-400">
              首页
            </Link>
            <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
            <span className="text-foreground-800">关于我们</span>
          </nav>
          <p className="mt-6 font-label text-xs uppercase tracking-[0.3em] text-accent-400">
            About Ethics Archive
          </p>
          <h1 className="mt-4 max-w-3xl text-2xl font-semibold leading-snug tracking-tight text-foreground-950 md:text-4xl">
            我们试着把「伦理」讲成
            <br className="hidden md:block" />
            一件可以被讨论的事
          </h1>
          <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
            日本伦理是一个专注日本伦理题材影视作品的资料型网站，由编辑与研究团队共同维护。我们整理作品档案、撰写剧情解析、记录行业动态，为影视爱好者、影评人与文化研究者提供可检索、可深读的内容。
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 py-12 md:px-12 md:py-16">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-14">
          <div className="lg:col-span-5">
            <h2 className="text-xl font-semibold text-foreground-950 md:text-2xl">站点定位</h2>
          </div>
          <div className="space-y-4 lg:col-span-7">
            <p className="text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
              伦理题材作品最容易被两种方式简化：一种是把复杂处境压缩成道德训诫，另一种是用刺激性的标签替代真正的讨论。我们想做的是第三种——把作品放回它所属的社会语境里，解释人物为什么这样选择，以及这些选择要付出什么代价。
            </p>
            <p className="text-sm leading-7 text-foreground-600 md:text-base md:leading-8">
              因此，本站的内容结构以「档案」为核心：每部作品都有完整的元信息与检索标签，每篇解析都遵循统一的四层框架。我们不提供播放与下载，只提供理解作品所需要的资料与视角。
            </p>
            <div className="rounded-lg border border-background-800 bg-background-900 px-5 py-5">
              <p className="flex items-start gap-3 text-sm leading-relaxed text-foreground-700">
                <i className="ri-information-line mt-0.5 text-accent-500" aria-hidden="true"></i>
                站内所有影视作品、导演、演员、机构与新闻事件均为虚构示例，仅用于演示影视资讯与解析的呈现方式，不对应任何真实作品或人物。
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="border-y border-background-800 bg-background-900">
        <div className="mx-auto max-w-[1440px] px-4 py-12 md:px-12 md:py-16">
          <div className="flex flex-col gap-3 border-b border-background-800 pb-5 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="font-label text-xs uppercase tracking-[0.26em] text-accent-500">
                Editorial Method
              </p>
              <h2 className="mt-2 text-xl font-semibold text-foreground-950 md:text-2xl">
                解析框架
              </h2>
            </div>
            <p className="max-w-md text-xs leading-relaxed text-foreground-500">
              每篇深度解析都按以下四个层次展开，避免用单一结论替代讨论过程。
            </p>
          </div>
          <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {framework.map((item) => (
              <div
                key={item.step}
                className="rounded-lg border border-background-800 bg-background-950/40 px-5 py-6"
              >
                <span className="font-label text-2xl leading-none text-accent-500">
                  {item.step}
                </span>
                <h3 className="mt-3 text-base font-medium text-foreground-950">{item.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground-600">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1440px] px-4 py-12 md:px-12 md:py-16">
        <div className="flex items-end justify-between border-b border-background-800 pb-5">
          <div>
            <p className="font-label text-xs uppercase tracking-[0.26em] text-accent-500">
              Team
            </p>
            <h2 className="mt-2 text-xl font-semibold text-foreground-950 md:text-2xl">团队</h2>
          </div>
          <span className="text-xs text-foreground-500">演示数据</span>
        </div>
        <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {team.map((member) => (
            <div
              key={member.name}
              className="rounded-lg border border-background-800 bg-background-900 px-5 py-6"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-md border border-background-700 text-accent-400">
                <i className="ri-user-line" aria-hidden="true"></i>
              </span>
              <h3 className="mt-4 text-sm font-medium text-foreground-950">{member.name}</h3>
              <p className="mt-1 text-xs text-accent-400">{member.role}</p>
              <p className="mt-2 text-xs leading-relaxed text-foreground-600">{member.bio}</p>
            </div>
          ))}
        </div>
      </section>

      <section id="contact" className="border-y border-background-800 bg-background-900">
        <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-8 px-4 py-12 md:px-12 md:py-16 lg:grid-cols-12">
          <div className="lg:col-span-4">
            <p className="font-label text-xs uppercase tracking-[0.26em] text-accent-500">
              Contact
            </p>
            <h2 className="mt-2 text-xl font-semibold text-foreground-950 md:text-2xl">
              联系我们
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-foreground-600">
              欢迎影评投稿、专题合作与内容纠错。为保证沟通效率，请在邮件标题中注明来意。
            </p>
          </div>
          <div className="lg:col-span-8">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <a
                href="mailto:editor@nihon-rinri-demo.jp"
                className="rounded-lg border border-background-800 bg-background-950/40 px-5 py-5 transition-colors hover:border-accent-500"
              >
                <p className="flex items-center gap-2 text-xs text-foreground-500">
                  <i className="ri-mail-line text-accent-500" aria-hidden="true"></i>
                  编辑与投稿
                </p>
                <p className="mt-2 break-all text-sm text-foreground-900">
                  editor@nihon-rinri-demo.jp
                </p>
                <p className="mt-1 text-xs text-foreground-500">一般在一周内回复</p>
              </a>
              <a
                href="mailto:research@nihon-rinri-demo.jp"
                className="rounded-lg border border-background-800 bg-background-950/40 px-5 py-5 transition-colors hover:border-accent-500"
              >
                <p className="flex items-center gap-2 text-xs text-foreground-500">
                  <i className="ri-file-text-line text-accent-500" aria-hidden="true"></i>
                  研究与专题合作
                </p>
                <p className="mt-2 break-all text-sm text-foreground-900">
                  research@nihon-rinri-demo.jp
                </p>
                <p className="mt-1 text-xs text-foreground-500">欢迎文化研究机构联系</p>
              </a>
            </div>
            <p className="mt-4 text-xs leading-relaxed text-foreground-500">
              以上邮箱为演示用地址，不对应真实收件人。站内不设账号与登录体系，收藏状态仅保存在本机浏览器。
            </p>
          </div>
        </div>
      </section>

      <section id="copyright" className="mx-auto max-w-[1440px] px-4 py-12 md:px-12 md:py-16">
        <h2 className="text-lg font-semibold text-foreground-950 md:text-xl">版权声明</h2>
        <div className="mt-4 space-y-3 text-sm leading-7 text-foreground-600">
          <p>
            本站为影视资讯与剧情解析的演示站点，站内文字内容由编辑团队撰写，所有作品名称、人物、机构与事件均为虚构，如有雷同纯属巧合。
          </p>
          <p>
            本站不提供任何影视作品的在线播放、下载或资源链接，亦不从事任何形式的版权内容分发。站内配图均为示意性生成图片，与真实作品无关。
          </p>
          <p>
            引用本站原创文字内容请注明来源；如需整篇转载或用于商业用途，请通过上方邮箱与我们联系。
          </p>
          <p className="text-xs text-foreground-500">
            © 2025 日本伦理 · Ethics Archive。保留所有权利。
          </p>
        </div>
      </section>
    </>
  );
}